/**
 * ViewSwitcher (Sprint 4.3.6).
 *
 * 3-tab pill that replaces the prior "Show as list" + "Show 16 folders"
 * twin-button cluster in the Pathway Editor header. The founder feedback
 * (2026-05-08): the editor "is parked at the bottom of long form where I
 * don't get the logic of how it is designed". Consolidating the pair of
 * toggle buttons into a single tablist drops one button row and clarifies
 * that the three views are mutually exclusive.
 *
 * Tabs:
 *   - canvas  - default. <PathwayCanvas> (React Flow).
 *   - list    - <PathwayListFallback> a11y mirror.
 *   - folders - <CareFlowComponentLeftRail> + folder editor (Sprint 4.3.4).
 *
 * Render contract:
 *   <span role="tablist" aria-label="Pathway editor view">
 *     <button role="tab" aria-selected="...">Canvas</button>
 *     <button role="tab" aria-selected="...">List</button>
 *     <button role="tab" aria-selected="...">16-folder JSON</button>
 *   </span>
 *
 * HC2 token-only. HC5 zero em / en dashes. HC6 a11y: tablist role, each
 * tab is `role="tab"` with `aria-selected` + `tabIndex` per the WAI-ARIA
 * tab pattern. Arrow keys are NOT intercepted (the surface is small and
 * the existing Mode toggle already uses radio semantics, so this stays
 * minimal); click + space + enter all work via native button defaults.
 */

export type PathwayEditorView = 'canvas' | 'list' | 'folders';

export interface ViewSwitcherOption {
  value: PathwayEditorView;
  label: string;
}

const OPTIONS: ReadonlyArray<ViewSwitcherOption> = [
  { value: 'canvas', label: 'Canvas' },
  { value: 'list', label: 'List' },
  { value: 'folders', label: '16-folder JSON' },
];

export interface ViewSwitcherProps {
  value: PathwayEditorView;
  onChange: (next: PathwayEditorView) => void;
  className?: string;
}

export function ViewSwitcher({ value, onChange, className }: ViewSwitcherProps) {
  return (
    <span
      role="tablist"
      aria-label="Pathway editor view"
      data-testid="pathway-editor-view-switcher"
      data-view={value}
      className={[
        'inline-flex items-center rounded-signal-pill border border-surface-border bg-surface-base p-0.5',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {OPTIONS.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            data-testid={`pathway-editor-view-switcher-${option.value}`}
            data-selected={selected ? 'true' : 'false'}
            onClick={() => onChange(option.value)}
            className={[
              'rounded-signal-pill px-gutter-md py-1 text-signal-xs font-semibold uppercase tracking-wide',
              'focus:outline-none focus:ring-2 focus:ring-signal-primary-500',
              selected
                ? 'bg-signal-primary-500 text-text-inverse'
                : 'text-text-secondary hover:text-text-primary',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {option.label}
          </button>
        );
      })}
    </span>
  );
}

export { OPTIONS as VIEW_SWITCHER_OPTIONS };
